<template>
  <!--
    Роутер рекурсивной ячейки склада (DisplayType 16, DataOfStorageItem).
    Каждая складская позиция (gen_el) разворачивается в свой внутренний элемент с
    собственным DisplayType, и внутренний элемент уходит на те же lite-компоненты,
    что и обычные ячейки в QTLiteRowCells.

    Каждая позиция оборачивается в блочный div (qtl-storage-sub): без него
    inner-элементы простых типов идут инлайном и склеиваются в одну строку.
  -->
  <template v-if="element.DisplayType === 16">
    <!-- Каждая складская позиция (gen_el) - своя блочная строка. Без обёртки
         inner-элементы простых типов (qtl-simple-html, иконки) были бы соседними
         инлайновыми спанами и склеивались в одну строку. Блок гарантирует перенос. -->
    <div v-for="(gen_el, i) in element.Data" :key="'sub_' + i" class="qtl-storage-sub">
      <!-- Внутренний элемент: данные позиции под её собственным DisplayType. -->
      <SimpleCellElementLite
        v-if="innerOf(gen_el).DisplayType === 0 || innerOf(gen_el).DisplayType === 6"
        :element="innerOf(gen_el)"
        :entity-id="entityId"
        :creator-id="creatorId"
        @refreshNeeded="$emit('refreshNeeded')"
      />

      <ParameterCellElementLite
        v-else-if="innerOf(gen_el).DisplayType === 2"
        :element="innerOf(gen_el)"
        :table-row-number="entityId"
        :column-index="columnIndex + '_' + i"
        :hovered-param-rec-id="hoveredParamRecId"
        :no-row-height-align="noRowHeightAlign"
        @changeHoverRecord="$emit('changeHoverRecord', $event)"
        @changeHeights="$emit('changeHeights', $event)"
      />

      <ChangeDateCellElementLite
        v-else-if="innerOf(gen_el).DisplayType === 7"
        :element="innerOf(gen_el)"
      />

      <span
        v-else-if="innerOf(gen_el).DisplayType === 12 || innerOf(gen_el).DisplayType === 15"
        class="qtl-icon-cell"
        v-html="iconHtml(innerOf(gen_el))"
      />

      <BillCellElementLite
        v-else-if="innerOf(gen_el).DisplayType === 18"
        :element="innerOf(gen_el)"
      />

      <CommonCellElementLite
        v-else-if="innerOf(gen_el).DisplayType === 16"
        :element="innerOf(gen_el)"
        :src-element="srcElement"
        :entity-id="entityId"
        :creator-id="creatorId"
        :WA="WA"
        :column-index="columnIndex + '_' + i"
        :hovered-param-rec-id="hoveredParamRecId"
        :no-row-height-align="noRowHeightAlign"
        @refreshNeeded="$emit('refreshNeeded')"
        @changeHoverRecord="$emit('changeHoverRecord', $event)"
        @changeHeights="$emit('changeHeights', $event)"
        @sourceItemsRequested="$emit('sourceItemsRequested', $event)"
      />

      <EntityCellElementLite
        v-else-if="[1, 3, 4, 8, 11, 13, 14, 17].includes(innerOf(gen_el).DisplayType)"
        :element="innerOf(gen_el)"
      />

      <JobCellElementLite
        v-else-if="innerOf(gen_el).DisplayType === 5 || innerOf(gen_el).DisplayType === 9"
        :element="innerOf(gen_el)"
      />

      <TagCellElementLite
        v-else-if="innerOf(gen_el).DisplayType === 10"
        :element="innerOf(gen_el)"
        :entity-id="entityId"
        @refreshNeeded="$emit('refreshNeeded')"
      />

      <!-- Ссылка на исходные элементы (DisplayType 19, IdList). Маршрут по типу, без
           привязки к Category. Событие клика пробрасываем наверх, entityId добавит
           родитель (QTLiteRowCells), как для верхнеуровневой ячейки. -->
      <SourceItemsCellElementLite
        v-else-if="innerOf(gen_el).DisplayType === 19"
        :element="innerOf(gen_el)"
        @sourceItemsRequested="$emit('sourceItemsRequested', $event)"
      />

      <!-- Прочие типы внутри позиции склада в данных не встречаются (бэк строго
           типизирован), поэтому показываем простое значение, а не сырой JSON. -->
      <SimpleCellElementLite
        v-else
        :element="innerOf(gen_el)"
        :entity-id="entityId"
        :creator-id="creatorId"
      />
    </div>
  </template>
</template>

<script setup lang="ts">
import type {
  CellDataOfStorageItem,
  TableDataCell,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import SimpleCellElementLite from "./SimpleCellElementLite.vue";
import ParameterCellElementLite from "./ParameterCellElementLite.vue";
import ChangeDateCellElementLite from "./ChangeDateCellElementLite.vue";
import BillCellElementLite from "./BillCellElementLite.vue";
import EntityCellElementLite from "./EntityCellElementLite.vue";
import JobCellElementLite from "./JobCellElementLite.vue";
import TagCellElementLite from "./TagCellElementLite.vue";
import SourceItemsCellElementLite from "./SourceItemsCellElementLite.vue";
import { formatIconHtml } from "./cellFormat";

const props = defineProps<{
  element: TableDataCell;
  srcElement?: any;
  entityId?: number;
  creatorId?: number;
  WA?: boolean;
  columnIndex?: string;
  hoveredParamRecId?: string | number;
  /**
   * Участвуют ли параметры внутри этой рекурсивной ячейки в построчном выравнивании
   * высот (см. ParameterCellElementLite.noRowHeightAlign и useRowParameterHeights).
   * Передаётся из QTLiteRowCells: для сгруппированной колонки (несколько subItems
   * стопкой друг под другом) выравнивать НЕ нужно - там параметры вертикально
   * независимы; для одиночной колонки (тип-16 параметр стоит сам по себе и
   * соседствует горизонтально с такими же в других колонках) выравнивать нужно,
   * как у верхнеуровневых параметров.
   */
  noRowHeightAlign?: boolean;
}>();

defineEmits<{
  (e: "refreshNeeded"): void;
  (e: "changeHoverRecord", payload: { recId: string | number | undefined; rowNum: number }): void;
  (e: "changeHeights", payload: any): void;
  (e: "sourceItemsRequested", payload: { ids: number[]; type: number }): void;
}>();

/**
 * Разворачиваем складскую позицию в её внутренний элемент: данные лежат в ItemData,
 * а тип рендеринга - в собственном DisplayType позиции. Category и Minimized
 * наследуем от внешней ячейки, как делал старый рекурсивный рендер.
 */
function innerOf(gen_el: CellDataOfStorageItem): TableDataCell {
  return {
    Data: gen_el.ItemData,
    DisplayType: gen_el.DisplayType,
    Category: props.element.Category,
    Minimized: false,
  } as TableDataCell;
}

function iconHtml(cell: TableDataCell): string {
  return formatIconHtml(cell).join("<br/>");
}
</script>

<style lang="scss" scoped>
.qtl-icon-cell {
  display: inline;
}

.qtl-storage-sub {
  display: block;
  min-width: 0;
  max-width: 100%;
}

.qtl-storage-sub:not(:last-child) {
  margin-bottom: 3px;
}
</style>
